//Libs
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  CellContextMenuEvent,
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  RowClickedEvent,
} from "ag-grid-community";
import isEqual from "lodash/isEqual";
import { Link } from "react-router-dom";

//Local
import {
  ExistingPurchaseOrder,
  getReadablePOStatus,
  OPODeliveryLocation,
} from "../../models/purchase-order";
import * as strings from "../../strings";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import { DollarCurrency } from "../../currency";
import { DbRead } from "../../database";
import SearchBox from "../../components/SearchBox";
import { PURCHASE_ORDERS_URL, NEW_PURCHASE_ORDER_URL } from "../../urls";
import {
  handleDeleteTableView,
  handleSetDefaultView,
  TableViewPreference,
} from "../../models/table-view-preferences";
import EditTableViewsDialog from "../../components/tables/EditTableViewsDialog";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import { ExistingVendor } from "../../models/vendor";
import { logger } from "../../logging";
import HeadingOne from "../../components/HeadingOne";
import { useAuthStore } from "../../store/firebase-auth";
import PurchaseOrderStatusCustomFilter from "../../components/tables/PurchaseOrderStatusCustomFilter";
import VendorsCustomFilter from "../../components/tables/VendorsCustomFilter";
import { renderPOStatus } from "../../assets/js/agGridUtils";

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";

interface Props {
  purchaseOrderList: ExistingPurchaseOrder[];
  siteKey: string;
  goToViewPurchaseOrder: (purchaseOrder: ExistingPurchaseOrder) => void;
  children: {
    DateRangePicker?: React.ReactNode;
  };
}

export default function PurchaseOrderListPage(props: Props) {
  const firebaseUser = useAuthStore((state) => state.firebaseUser);

  const [tableViewPreferenceDialogOpen, setTableViewPreferenceDialogOpen] =
    useState(false);
  const [editViewDialogsOpen, setEditViewDialogsOpen] = useState(false);
  const [tableViewPreferenceList, setTableViewPreferenceList] = useState<
    TableViewPreference[]
  >([]);
  const [selectedView, setSelectedView] = useState<TableViewPreference | null>(
    null,
  );
  const [vendorList, setVendorList] = useState<ExistingVendor[]>([]);

  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    async function getVendorsForPurchaseOrders() {
      try {
        const vendors = await DbRead.vendors.getAll(props.siteKey);
        setVendorList(vendors);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    }

    getVendorsForPurchaseOrders();
  }, [props.siteKey]);

  const actionButtons = (
    <Fragment>
      {/* <BaseButtonPrimary onClick={() => setTableViewPreferenceDialogOpen(true)}>
        {strings.SAVE_TABLE_VIEW}
      </BaseButtonPrimary>
      <BaseButtonSecondary onClick={() => setEditViewDialogsOpen(true)}>
        {strings.EDIT_TABLE_VIEWS}
      </BaseButtonSecondary> */}
      <Link to={NEW_PURCHASE_ORDER_URL}>
        <BaseButtonPrimary>{strings.NEW_PURCHASE_ORDER}</BaseButtonPrimary>
      </Link>
    </Fragment>
  );

  const editTableViewsDialog = (
    <EditTableViewsDialog
      isDialogOpen={editViewDialogsOpen}
      closeDialog={() => setEditViewDialogsOpen(false)}
      deleteTableView={(viewToDelete: TableViewPreference) =>
        handleDeleteTableView(
          viewToDelete,
          setTableViewPreferenceList,
          setSelectedView,
        )
      }
      handleSetDefaultView={(viewToSetAsDefault: TableViewPreference) =>
        handleSetDefaultView(
          viewToSetAsDefault,
          setTableViewPreferenceList,
          setSelectedView,
        )
      }
      savedViewList={tableViewPreferenceList}
    />
  );

  return (
    <div className="space-y-2 lg:space-y-6">
      <HeadingOne>{strings.PURCHASE_ORDERS}</HeadingOne>
      <div className="ag-theme-alpine isolate h-[1000px] max-h-[68vh] pb-6">
        <PurchaseOrderListTable
          purchaseOrderList={props.purchaseOrderList}
          goToViewPurchaseOrder={props.goToViewPurchaseOrder}
          actionButtons={actionButtons}
          gridRef={gridRef}
          setSelectedView={setSelectedView}
          setTableViewPreferenceList={setTableViewPreferenceList}
          selectedView={selectedView}
          vendorList={vendorList}
          children={props.children}
        />
        {editTableViewsDialog}
      </div>
    </div>
  );
}

interface PurchaseOrderListTableProps {
  purchaseOrderList: Props["purchaseOrderList"];
  goToViewPurchaseOrder: Props["goToViewPurchaseOrder"];
  actionButtons: React.ReactNode;
  gridRef: React.MutableRefObject<any>;
  setSelectedView: React.Dispatch<
    React.SetStateAction<TableViewPreference | null>
  >;
  setTableViewPreferenceList: React.Dispatch<
    React.SetStateAction<TableViewPreference[]>
  >;
  selectedView: TableViewPreference | null;
  vendorList: ExistingVendor[];
  children: {
    DateRangePicker?: React.ReactNode;
  };
}

const commonColDefs: ColDef = {
  resizable: true,
  filter: true,
  sortable: true,
  headerClass:
    "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
  tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
  tooltipField: "notes",
};

/* TABLE COMPONENT */
const PurchaseOrderListTable = memo(
  ({
    purchaseOrderList,
    goToViewPurchaseOrder,
    actionButtons,
    gridRef,
    setSelectedView,
    setTableViewPreferenceList,
    selectedView,
    vendorList,
    children,
  }: PurchaseOrderListTableProps) => {
    const [gridReady, setGridReady] = useState(false);

    const onGridReady = useCallback(() => {
      setGridReady(true);
    }, []);

    // useLayoutEffect(() => {
    //   if (gridRef.current && gridReady) {
    //     gridRef.current.api?.sizeColumnsToFit();
    //     getTableViewPreferenceList("purchaseOrderList", setTableViewPreferenceList);
    //     const defaultView = getDefaultViewForThisTable("purchaseOrderList");
    //     setInitialView(defaultView, gridRef, setSelectedView);
    //   }
    // }, [gridRef, setSelectedView, setTableViewPreferenceList, gridReady]);

    useEffect(() => {
      if (selectedView && gridReady) {
        gridRef.current?.api.applyColumnState({
          state: selectedView.settings,
          applyOrder: true,
        });
        gridRef.current?.api.setFilterModel(selectedView.filter);
        logger.info("set column state for", selectedView.name);
      }
    }, [gridRef, selectedView, gridReady]);

    const onFilterTextBoxChanged = useCallback(() => {
      if (gridReady) {
        gridRef.current?.api.setQuickFilter(
          (document.getElementById("filter-text-box") as HTMLInputElement)
            .value,
        );
      }
    }, [gridRef, gridReady]);

    const renderVendorName = (params: ICellRendererParams): JSX.Element => {
      const currentVendor = vendorList.find(
        (vendor) => vendor.id === params.data.vendorID,
      );
      return <div className="capitalize">{currentVendor?.name ?? ""}</div>;
    };

    const renderLineItems = (params: ICellRendererParams): JSX.Element => {
      const lineItems = params.data.lineItems;
      if (!lineItems || !lineItems.length) return <div>No items</div>;

      return (
        <div className="space-y-1">
          {lineItems.slice(0, 2).map((item: any, index: number) => (
            <div
              key={index}
              className="max-w-md truncate text-sm leading-tight"
            >
              {item.quantity} x {item.title}
              {item.description && (
                <span className="ml-1 text-xs text-gray-500">
                  - {item.description}
                </span>
              )}
            </div>
          ))}
          {lineItems.length > 2 && (
            <div className="text-xs italic text-gray-500">
              +{lineItems.length - 2} more items
            </div>
          )}
        </div>
      );
    };

    const renderDeliveryLocation = (
      params: ICellRendererParams,
    ): JSX.Element => {
      const deliveryLocation = params.data.deliveryLocation;

      if (deliveryLocation === OPODeliveryLocation.OFFICE) {
        return <div>Office</div>;
      } else if (deliveryLocation === OPODeliveryLocation.VENDOR) {
        return <div>Vendor</div>;
      } else if (deliveryLocation === OPODeliveryLocation.JOB_ADDRESS) {
        return <div>Job Address</div>;
      } else if (deliveryLocation === OPODeliveryLocation.OTHER) {
        const address = [
          params.data.deliveryAddressLine1,
          params.data.deliveryAddressLine2,
          params.data.deliveryCity,
          params.data.deliveryState,
          params.data.deliveryZipCode,
        ]
          .filter(Boolean)
          .join(", ");
        return <div className="text-sm">{address || "Other"}</div>;
      }

      return <div>{deliveryLocation || "—"}</div>;
    };

    // Function to make line items searchable
    const getLineItemsQuickFilterText = (
      params: GetQuickFilterTextParams,
    ): string => {
      const lineItems = params.data.lineItems;
      if (!lineItems || !lineItems.length) return "";

      return lineItems
        .map((item: any) => {
          const parts = [item.title];
          if (item.description) parts.push(item.description);
          if (item.vendorPartNumber) parts.push(item.vendorPartNumber);
          return parts.join(" ");
        })
        .join(" ");
    };

    const columnDefs: ColDef[] = [
      {
        field: "poNumber",
        headerName: "PO #",
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
        tooltipField: "poNumber",
        width: 150,
        minWidth: 100,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: renderPOStatus,
        width: 150,
        minWidth: 120,
        filter: PurchaseOrderStatusCustomFilter,
        filterParams: {
          buttons: ["reset"],
        },
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "vendorID",
        headerName: "Vendor",
        cellRenderer: renderVendorName,
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          const currentVendor = vendorList.find(
            (vendor) => vendor.id === params.data.vendorID,
          );
          return currentVendor ? currentVendor.name : "";
        },
        width: 200,
        minWidth: 150,
        filter: VendorsCustomFilter,
        filterParams: {
          vendorList: vendorList,
          buttons: ["reset"],
        },
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "vendorReferenceNumber",
        headerName: "Vendor Ref #",
        width: 150,
        minWidth: 120,
        cellRenderer: (params: ICellRendererParams) =>
          params.data.vendorReferenceNumber || "—",
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "issueDate",
        headerName: "Issue Date",
        cellRenderer: (params: ICellRendererParams) =>
          getReadableTimestamp(params.data.issueDate),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getReadableTimestamp(params.data.issueDate),
        width: 150,
        minWidth: 120,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "expectedDeliveryDate",
        headerName: "Expected Delivery Date",
        cellRenderer: (params: ICellRendererParams) =>
          getReadableTimestamp(params.data.expectedDeliveryDate),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getReadableTimestamp(params.data.expectedDeliveryDate),
        width: 150,
        minWidth: 120,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "deliveryLocation",
        headerName: "Delivery Location",
        cellRenderer: renderDeliveryLocation,
        width: 180,
        minWidth: 150,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "lineItems",
        headerName: "Items",
        cellRenderer: renderLineItems,
        getQuickFilterText: getLineItemsQuickFilterText,
        width: 300,
        minWidth: 200,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "totalAmount",
        headerName: "Total Amount",
        cellRenderer: getFormattedAmount,
        getQuickFilterText: getFormattedAmount,
        width: 150,
        minWidth: 120,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "notes",
        headerName: "Notes",
        tooltipValueGetter: (params) => params.data.notes,
        width: 200,
        minWidth: 150,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
    ];

    return (
      <Fragment>
        <div className="flex flex-col-reverse justify-between gap-6 md:-mt-4 md:flex-row md:items-end lg:mt-0">
          <SearchBox
            onInput={onFilterTextBoxChanged}
            widthClasses="w-full max-w-sm"
          />
          <span className="flex flex-wrap items-center justify-end gap-4 md:flex-col md:items-end lg:flex-row lg:items-center">
            {children?.DateRangePicker}
            <div className="flex gap-4">{actionButtons}</div>
          </span>
        </div>
        <AgGridReact
          reactiveCustomComponents
          ref={gridRef}
          onGridReady={onGridReady}
          tooltipShowDelay={1}
          defaultColDef={commonColDefs}
          className="mt-5 shadow"
          rowData={purchaseOrderList}
          animateRows={true}
          rowSelection="single"
          rowHeight={75}
          cacheQuickFilter={true}
          columnDefs={columnDefs}
          onRowClicked={(event: RowClickedEvent) =>
            goToViewPurchaseOrder(event.data)
          }
          onCellContextMenu={(event: CellContextMenuEvent<any, any>) => {
            const url = `${PURCHASE_ORDERS_URL}/${event.data.id}`;
            return window.open(url, "_blank");
          }}
        />
      </Fragment>
    );
  },
  (previous, next) => {
    const isPurchaseOrderListTheSame = isEqual(
      previous.purchaseOrderList,
      next.purchaseOrderList,
    );

    const isVendorListTheSame = isEqual(previous.vendorList, next.vendorList);

    return isPurchaseOrderListTheSame && isVendorListTheSame;
  },
);

function getFormattedAmount(
  params: ICellRendererParams | GetQuickFilterTextParams,
): string {
  if (typeof params.data.totalAmount !== "number") return "";
  return DollarCurrency.format(params.data.totalAmount);
}

function getReadableTimestamp(timestamp: unknown): string {
  if (timestamp != null) {
    return convertToReadableTimestamp(timestamp);
  } else {
    return "";
  }
}
